// ============================================================================
// Public signer page — /sign/:token (NO auth shell; the token IS the grant).
// Flow: view document -> verify identity (email OTP) -> adopt signature ->
// sign -> confirmation. Mobile-first (380px), 56px touch targets.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  CheckCircle2, FileText, Loader2, Lock, ShieldCheck,
} from 'lucide-react';
import {
  EsignError, SignerMeta, esignPublic, fetchSigningPdf,
} from './lib';
import { EnvelopeSignerFlow } from './envelope-signer';
import type { EnvSignerMeta } from './envelope-lib';

type Step = 'loading' | 'invalid' | 'view' | 'verify' | 'sign' | 'done';

export function SignerPage() {
  const { token = '' } = useParams<{ token: string }>();
  const [step, setStep] = useState<Step>('loading');
  const [meta, setMeta] = useState<SignerMeta | null>(null);
  const [envMeta, setEnvMeta] = useState<EnvSignerMeta | null>(null);
  const [fatal, setFatal] = useState<string>('This signing link is not valid.');
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // verify step
  const [otpSent, setOtpSent] = useState(false);
  const [otpBusy, setOtpBusy] = useState(false);
  const [code, setCode] = useState('');
  const [verifyError, setVerifyError] = useState<string | null>(null);

  // sign step
  const [signedName, setSignedName] = useState('');
  const [signerTitle, setSignerTitle] = useState('');
  const [consent, setConsent] = useState(false);
  const [signBusy, setSignBusy] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);

  const pdfUrlRef = useRef<string | null>(null);

  const loadPdf = useCallback(async () => {
    try {
      const url = await fetchSigningPdf(token);
      pdfUrlRef.current = url;
      setPdfUrl(url);
    } catch {
      setPdfError('The document preview could not be loaded.');
    }
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await esignPublic<SignerMeta | EnvSignerMeta>({ action: 'meta', token });
        if (cancelled) return;
        if ('scope' in m && m.scope === 'envelope') {
          // Envelope-scoped token — the guided walk owns the rest of the flow.
          setEnvMeta(m as EnvSignerMeta);
          return;
        }
        setMeta(m as SignerMeta);
        setSignedName(m.signer_name ?? '');
        // Delivery-based verification: an emailed link arrives already
        // verified — skip the interstitial verify step entirely.
        if (m.state === 'completed') setStep('done');
        else if ((m as SignerMeta).verified) setStep('sign');
        else setStep('view');
        void loadPdf();
      } catch (e) {
        if (cancelled) return;
        if (e instanceof EsignError && e.status === 410) {
          setFatal('This signing link has expired. Ask Terminal Cleaning Services to send a new one.');
        }
        setStep('invalid');
      }
    })();
    return () => {
      cancelled = true;
      if (pdfUrlRef.current) URL.revokeObjectURL(pdfUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function requestOtp() {
    setOtpBusy(true);
    setVerifyError(null);
    try {
      await esignPublic({ action: 'request_otp', token });
      setOtpSent(true);
    } catch (e) {
      setVerifyError(
        e instanceof EsignError && e.code === 'otp_delivery_unavailable'
          ? 'Code delivery is not available right now. Contact your TCS representative.'
          : 'Could not send the code. Try again.',
      );
    } finally {
      setOtpBusy(false);
    }
  }

  async function verifyOtp() {
    setOtpBusy(true);
    setVerifyError(null);
    try {
      await esignPublic({ action: 'verify_otp', token, code: code.trim() });
      setMeta((m) => (m ? { ...m, verified: true } : m));
      setStep('sign');
    } catch (e) {
      if (e instanceof EsignError) {
        if (e.code === 'wrong_code') setVerifyError('That code is not correct.');
        else if (e.code === 'otp_expired') { setVerifyError('That code expired — request a new one.'); setOtpSent(false); }
        else if (e.code === 'otp_locked') { setVerifyError('Too many attempts — request a new code.'); setOtpSent(false); }
        else setVerifyError('Verification failed. Try again.');
      } else setVerifyError('Verification failed. Try again.');
    } finally {
      setOtpBusy(false);
    }
  }

  async function doSign() {
    setSignBusy(true);
    setSignError(null);
    try {
      await esignPublic({
        action: 'sign',
        token,
        signed_name: signedName.trim(),
        signer_title: signerTitle.trim() || undefined,
        consent,
      });
      setStep('done');
    } catch (e) {
      setSignError(
        e instanceof EsignError && e.message ? e.message : 'Signing failed. Try again.',
      );
    } finally {
      setSignBusy(false);
    }
  }

  const header = (
    <header className="border-b border-tcs-border bg-tcs-raised px-4 py-3">
      <div className="mx-auto flex max-w-2xl items-center gap-2">
        <Lock className="h-4 w-4 text-tcs-text-muted" aria-hidden />
        <div>
          <p className="text-sm font-semibold text-tcs-text">
            Terminal Cleaning Services — secure signing
          </p>
          {meta && (
            <p className="text-xs text-tcs-text-muted">
              Contract {meta.contract_number ?? ''}
              {meta.account_name ? ` · ${meta.account_name}` : ''}
              {meta.facility_name ? ` · ${meta.facility_name}` : ''}
            </p>
          )}
        </div>
      </div>
    </header>
  );

  if (envMeta) {
    // Header band: branding, document name, who sent it, and how long the
    // link lasts — everything the signer needs to trust the page, above the
    // fold, before anything else.
    const daysLeft = envMeta.expires_at
      ? Math.ceil(
        (new Date(envMeta.expires_at).getTime() - Date.now()) / 86_400_000,
      )
      : null;
    return (
      <div className="min-h-full bg-tcs-surface">
        <header className="border-b border-tcs-border bg-tcs-raised px-4 py-3">
          <div className="mx-auto flex max-w-2xl items-start gap-2">
            <Lock className="mt-0.5 h-4 w-4 shrink-0 text-tcs-text-muted" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-tcs-text">
                Terminal Cleaning Services — secure signing
              </p>
              <p className="truncate text-xs text-tcs-text-muted">
                {envMeta.title}
                {envMeta.sender_name ? ` · Sent by ${envMeta.sender_name}` : ''}
              </p>
            </div>
            {daysLeft != null && daysLeft > 0 && (
              <span className="shrink-0 rounded-full bg-tcs-bg px-2 py-1 text-[11px] text-tcs-text-muted">
                Link expires in {daysLeft} day{daysLeft === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </header>
        <EnvelopeSignerFlow token={token} meta={envMeta} />
      </div>
    );
  }

  if (step === 'loading') {
    return (
      <div className="flex min-h-full items-center justify-center bg-tcs-surface">
        <Loader2 className="h-8 w-8 animate-spin text-tcs-text-muted" aria-label="Loading" />
      </div>
    );
  }

  if (step === 'invalid') {
    return (
      <div className="flex min-h-full items-center justify-center bg-tcs-surface p-6">
        <div className="max-w-sm rounded-md border border-tcs-border bg-tcs-raised p-6 text-center">
          <p className="text-base font-semibold text-tcs-text">Link not valid</p>
          <p className="mt-2 text-sm text-tcs-text-muted">{fatal}</p>
        </div>
      </div>
    );
  }

  if (step === 'done') {
    return (
      <div className="min-h-full bg-tcs-surface">
        {header}
        <main className="mx-auto max-w-2xl p-6 text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-st-ok-fg" aria-hidden />
          <h1 className="mt-4 text-xl font-semibold text-tcs-text">Document signed</h1>
          <p className="mt-2 text-sm text-tcs-text-muted">
            Thank you. A sealed copy with the signature certificate is on file
            with Terminal Cleaning Services.
          </p>
          {pdfUrl && (
            <a
              href={pdfUrl}
              download={`TCS-contract-${meta?.contract_number ?? 'signed'}.pdf`}
              className="mt-6 inline-block rounded-md bg-tcs-accent-ink px-6 py-3 text-sm font-semibold text-tcs-accent-on"
            >
              Download document
            </a>
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-full bg-tcs-surface pb-28">
      {header}
      <main className="mx-auto max-w-2xl p-4">
        {/* Document */}
        <section aria-label="Document" className="rounded-md border border-tcs-border bg-tcs-raised">
          {pdfUrl ? (
            <iframe title="Contract document" src={pdfUrl} className="h-[60vh] w-full rounded-md" />
          ) : (
            <div className="flex h-[40vh] flex-col items-center justify-center gap-2 text-tcs-text-muted">
              <FileText className="h-8 w-8" aria-hidden />
              <p className="text-sm">{pdfError ?? 'Loading document…'}</p>
            </div>
          )}
        </section>

        {/* Step content */}
        {step === 'view' && (
          <section className="mt-4 rounded-md border border-tcs-border bg-tcs-raised p-4">
            <h2 className="text-base font-semibold text-tcs-text">Review, then verify your identity</h2>
            <p className="mt-1 text-sm text-tcs-text-muted">
              We will send a one-time code to {meta?.signer_email_masked} to confirm it is you.
            </p>
            <button
              type="button"
              onClick={() => { setStep(meta?.verified ? 'sign' : 'verify'); }}
              className="mt-4 w-full rounded-md bg-tcs-accent-ink px-4 py-4 text-sm font-semibold text-tcs-accent-on"
            >
              {meta?.verified ? 'Continue to sign' : 'Continue'}
            </button>
          </section>
        )}

        {step === 'verify' && (
          <section className="mt-4 rounded-md border border-tcs-border bg-tcs-raised p-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-tcs-accent-ink" aria-hidden />
              <h2 className="text-base font-semibold text-tcs-text">Verify your identity</h2>
            </div>
            {!otpSent ? (
              <>
                <p className="mt-2 text-sm text-tcs-text-muted">
                  Send a 6-digit code to {meta?.signer_email_masked}.
                </p>
                <button
                  type="button"
                  onClick={requestOtp}
                  disabled={otpBusy}
                  className="mt-4 w-full rounded-md bg-tcs-accent-ink px-4 py-4 text-sm font-semibold text-tcs-accent-on disabled:opacity-50"
                >
                  {otpBusy ? 'Sending…' : 'Email me the code'}
                </button>
              </>
            ) : (
              <>
                <label htmlFor="otp" className="mt-3 block text-sm text-tcs-text-muted">
                  Enter the 6-digit code sent to {meta?.signer_email_masked}
                </label>
                <input
                  id="otp"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  className="mt-2 w-full rounded-md border border-tcs-border px-4 py-4 text-center text-2xl tracking-[0.4em]"
                />
                <button
                  type="button"
                  onClick={verifyOtp}
                  disabled={otpBusy || code.length !== 6}
                  className="mt-3 w-full rounded-md bg-tcs-accent-ink px-4 py-4 text-sm font-semibold text-tcs-accent-on disabled:opacity-50"
                >
                  {otpBusy ? 'Checking…' : 'Verify'}
                </button>
                <button
                  type="button"
                  onClick={requestOtp}
                  disabled={otpBusy}
                  className="mt-2 w-full px-4 py-3 text-sm text-tcs-text-muted underline"
                >
                  Send a new code
                </button>
              </>
            )}
            {verifyError && (
              <p role="alert" className="mt-3 text-sm text-st-danger-fg">{verifyError}</p>
            )}
          </section>
        )}

        {step === 'sign' && (
          <section className="mt-4 rounded-md border border-tcs-border bg-tcs-raised p-4">
            <h2 className="text-base font-semibold text-tcs-text">Adopt your signature</h2>
            <label htmlFor="signed-name" className="mt-3 block text-sm text-tcs-text-muted">
              Full legal name
            </label>
            <input
              id="signed-name"
              value={signedName}
              onChange={(e) => setSignedName(e.target.value)}
              className="mt-1 w-full rounded-md border border-tcs-border px-4 py-3 text-base"
            />
            <label htmlFor="signer-title" className="mt-3 block text-sm text-tcs-text-muted">
              Title (optional)
            </label>
            <input
              id="signer-title"
              value={signerTitle}
              onChange={(e) => setSignerTitle(e.target.value)}
              className="mt-1 w-full rounded-md border border-tcs-border px-4 py-3 text-base"
            />
            {signedName.trim().length >= 2 && (
              <div className="mt-4 rounded-md border border-dashed border-tcs-border bg-tcs-bg p-4 text-center">
                <p className="font-serif text-2xl italic text-tcs-text">{signedName.trim()}</p>
                <p className="mt-1 text-xs text-tcs-text-muted">Adopted signature</p>
              </div>
            )}
            <label className="mt-4 flex items-start gap-3">
              <input
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 h-5 w-5"
              />
              <span className="text-sm text-tcs-text">
                I agree that my electronic signature is the legal equivalent of my
                handwritten signature, and I consent to do business electronically
                with Terminal Cleaning Services.
              </span>
            </label>
            <button
              type="button"
              onClick={doSign}
              disabled={signBusy || !consent || signedName.trim().length < 2}
              className="mt-4 w-full rounded-md bg-tcs-accent-ink px-4 py-4 text-base font-semibold text-tcs-accent-on disabled:opacity-50"
            >
              {signBusy ? 'Sealing document…' : 'Sign document'}
            </button>
            {signError && (
              <p role="alert" className="mt-3 text-sm text-st-danger-fg">{signError}</p>
            )}
          </section>
        )}
      </main>
    </div>
  );
}
